import React, { useEffect, useRef, useState } from 'react';
import './ShipCard.css';
import ShipUpgradeDisplay from './ShipUpgradeDisplay';
import type { ShipDto } from '../../types/ship-types';
import { ShipType, ShipStatus, ShipLocation, getShipLocationString, getShipTypeString } from '../../types/ship-types';

// Helper functions for formatting and status
const formatNumber = (num: number) => parseFloat(num as any).toLocaleString('en-US', { maximumFractionDigits: 1 });

const statusMap: Record<ShipStatus, { name: string; class: string }> = {
  [ShipStatus.AwaitingOrders]: { name: 'Idle', class: 'idle' },
  [ShipStatus.Mining]: { name: 'Mining', class: 'mining' },
  [ShipStatus.WarpingToMothership]: { name: 'Warping', class: 'warping' },
  [ShipStatus.WarpingToAsteroidBelt]: { name: 'Warping', class: 'warping' },
  [ShipStatus.OffloadingCargo]: { name: 'Unloading', class: 'offloading' },
  [ShipStatus.UnloadingBeforeAwaitingOrders]: { name: 'Unloading', class: 'offloading' },
  [ShipStatus.Upgrading]: { name: 'Upgrading', class: 'upgrading' },
  [ShipStatus.WarpingToMothershipForUpgrade]: { name: 'Warping', class: 'warping' },
  [ShipStatus.MiningToHauler]: { name: 'Mining to Hauler', class: 'mining' },
  [ShipStatus.Repairing]: { name: 'Repairing', class: 'repairing' },
  [ShipStatus.WarpingToMothershipForRepair]: { name: 'Warping', class: 'warping' },
  [ShipStatus.WarpingToMothershipForOrders]: { name: 'Warping', class: 'warping' },
  [ShipStatus.Disabled]: { name: 'Disabled', class: 'disabled' },
};

const ShipCard: React.FC<{
  ship: ShipDto;
  onWarp: () => void;
  onRecall: () => void;
  onPurchaseUpgrade: (branchType: string | number, upgradeName: string) => void;
  playerOre: number;
}> = ({ ship, onWarp, onRecall, onPurchaseUpgrade, playerOre }) => {
  const badge = statusMap[ship.status] || { name: String(ship.status), class: '' };
  const typeName = getShipTypeString(ship.type) || String(ship.type);

  // Cargo calculation
  const cargoPercent = Math.min(100, Math.max(0, (ship.currentCargoLoad / (ship.effectiveCargoCapacity)) * 100)) || 0;

  // ETA countdown
  const [eta, setEta] = useState<number | null>(null);
  const etaStartTimeRef = useRef<number | null>(null);
  const originalEtaRef = useRef<number | null>(null);

  useEffect(() => {
    // If ship.estimatedTimeToComplete is present and > 0, start animation
    if (typeof ship.estimatedTimeToComplete === 'number' && ship.estimatedTimeToComplete > 0) {
      if (!etaStartTimeRef.current || ship.estimatedTimeToComplete !== originalEtaRef.current) {
        etaStartTimeRef.current = Date.now();
        originalEtaRef.current = ship.estimatedTimeToComplete;
      }
      let frameId: number;
      const animate = () => {
        const elapsed = (Date.now() - (etaStartTimeRef.current || 0)) / 1000;
        const newEta = Math.max(0, (originalEtaRef.current || 0) - elapsed);
        setEta(newEta);
        if (newEta > 0) {
          frameId = requestAnimationFrame(animate);
        }
      };
      animate();
      return () => cancelAnimationFrame(frameId);
    } else {
      setEta(null);
      etaStartTimeRef.current = null;
      originalEtaRef.current = null;
    }
  }, [ship.estimatedTimeToComplete, ship.status, ship.location]);

  // Format ETA display
  let etaDisplay = '';
  if (eta !== null && eta > 0) {
    if (eta > 100) {
      etaDisplay = `ETA: ${eta.toFixed(0)}s`;
    } else if (eta > 10) {
      etaDisplay = `ETA: ${eta.toFixed(1)}s`;
    }
    else if (eta > 0) {
      etaDisplay = `ETA: ${eta.toFixed(2)}s`;
    }
  }

  // Action buttons
  let warpBtn: React.ReactNode = null;
  let recallBtn: React.ReactNode = null;

  // Show 'Mine' only if at Mothership, 'Return' only if at Asteroid Belt
  if (ship.status !== ShipStatus.WarpingToAsteroidBelt && ship.status !== ShipStatus.WarpingToMothership) {
    if (ship.location === ShipLocation.Mothership) {
      warpBtn = <button onClick={onWarp} style={{ marginLeft: 'var(--space-sm)' }}>Mine</button>;
    } else if (ship.location === ShipLocation.AsteroidBelt) {
      recallBtn = <button onClick={onRecall} className="danger" style={{ marginLeft: 'var(--space-sm)' }}>Return</button>;
    }
  }

  // Upgrades section
  let upgradesHtml = <div style={{ marginTop: 'var(--space-sm)' }}><span className="text-dim">No upgrades available</span></div>;

  // Check if any upgrade is pending/queued
  const hasPendingUpgrade = ship.pendingUpgrade !== null && ship.pendingUpgrade !== undefined;
  // Use upgradeTree branches
  if (ship.upgradeTree && Array.isArray(ship.upgradeTree.branches) && ship.upgradeTree.branches.length > 0) {
    // For each branch, show only the next not-yet-unlocked upgrade (or the last one if all unlocked)
    const nextUpgrades = ship.upgradeTree.branches.map(branch => {
      const next = branch.upgrades.find(u => !u.isUnlocked);
      return next || branch.upgrades[branch.upgrades.length - 1];
    }).filter(Boolean);

    upgradesHtml = (
      <div>
        <ShipUpgradeDisplay
          upgrades={nextUpgrades}
          upgradeTree={ship.upgradeTree}
          onUpgradeClick={(upgrade) => {
            // Find branch for this upgrade
            const branch = ship.upgradeTree?.branches.find(b => b.upgrades.some(u => u.id === upgrade.id));
            if (branch) {
              onPurchaseUpgrade(branch.originalBranchType, upgrade.name);
            }
          }}
          disabled={hasPendingUpgrade}
          queuedUpgradeId={ship.pendingUpgrade?.id || null}
          playerOre={playerOre}
          baseMiningSpeed={ship.baseMiningSpeed}
          baseWarpSpeed={ship.baseWarpSpeed}
          baseCargoCapacity={ship.baseCargoCapacity}
        />
      </div>
    );
  }

  // Yield and ETA display (now in the same section as location/status)
  let statusLocationHtml = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-xs)',
      marginBottom: 'var(--space-xs)'
    }}>      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', overflow: 'hidden' }}>
          <span className="text-accent" style={{ minWidth: 50, maxWidth: 50, fontWeight: 'var(--font-weight-bold)', flexShrink: 0 }}>Status:</span>
          <span className={`status-badge ${badge.class}`}>{badge.name}</span>
        </div>
        <div className="eta"
          style={{
            minWidth: 90, maxWidth: 90, textAlign: 'right', color: 'var(--color-warning)',
            fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)',
            flexShrink: 0
          }}>
          {etaDisplay || '\u00A0'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <span className="text-accent" style={{ fontWeight: 'var(--font-weight-bold)' }}>Location:</span>
          <span className="text-primary">{getShipLocationString(ship.location) || '-'}</span>
        </div>
      </div>
    </div>
  );

  // Add display for yield, warp speed, and unload time in a single line, equally divided
  let statsLineHtml = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0',
      marginBottom: 'var(--space-xs)',
    }}>
      <div style={{ flex: 1, textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-success)', fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Yield: {formatNumber(ship.effectiveMiningYield)} m³/s
      </div>
      {/* <div style={{ flex: 1, textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-warning)', fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Unload: {formatNumber(ship.effectiveUnloadTime ?? 0)}s
      </div> */}
      <div style={{ flex: 1, textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-accent-blue)', fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Warp: {formatNumber(ship.effectiveWarpSpeed ?? ship.baseWarpSpeed)} au/s
      </div>
    </div>
  );
  return (
    <div className="ship-card">
      <div className="ship-header">
        <span className="ship-name">{ship.name || typeName}</span>
        {warpBtn}
        {recallBtn}
      </div>
      <div className="divider"></div>
      <div className="ship-content-container">
        {statusLocationHtml}
        {statsLineHtml}

        {/* Shield and Hull bars side by side */}
        <div className="ship-bars-container" style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-xs)' }}>

          {/* Hull bar */}
          <div className="ship-hull-bar" style={{ flex: 1 }}>
            <div className="ship-hull-fill" style={{ width: `${Math.min(100, Math.max(0, (ship.currentHullValue / ship.baseHullCapacity) * 100))}%` }}></div>
            <div className="ship-hull-text">
              <span style={{ marginLeft: 'var(--space-sm)' }}>Hull</span>
              <span style={{ marginRight: 'var(--space-md)' }}>
                {formatNumber(ship.currentHullValue)} / {formatNumber(ship.baseHullCapacity)}
              </span>
            </div>
          </div>
          {/* Shield bar */}
          <div className="ship-shield-bar" style={{ flex: 1 }}>
            <div className="ship-shield-fill" style={{ width: `${Math.min(100, Math.max(0, (ship.currentShieldValue / ship.baseShieldCapacity) * 100))}%` }}></div>
            <div className="ship-shield-text">
              <span style={{ marginLeft: 'var(--space-sm)' }}>Shields</span>
              <span style={{ marginRight: 'var(--space-md)' }}>
                {formatNumber(ship.currentShieldValue)} / {formatNumber(ship.baseShieldCapacity)}
              </span>
            </div>
          </div>
        </div>
        {/* Cargo bar with overlay text */}
        <div className="ship-cargo-bar">
          <div className="ship-cargo-fill" style={{ width: `${cargoPercent}%` }}></div>
          <div className="ship-cargo-text">
            <span style={{ marginLeft: 'var(--space-xs)' }}>Cargo</span>
            <span style={{ marginRight: 'var(--space-xs)' }}>
              {formatNumber(ship.currentCargoLoad)} / {formatNumber(ship.effectiveCargoCapacity)}
            </span>
          </div>
        </div>
      </div>
      <div className="divider"></div>
      <div className="ship-upgrades">
        {upgradesHtml}
      </div>
    </div>
  );
};

export default ShipCard;
